import React, { useState } from 'react';
import { Link } from 'react-router';
import { PageHero } from '../components/PageHero';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { PreviewDisclaimer } from '../components/PreviewDisclaimer';
import { EmptyState } from '../components/EmptyState';
import { StatusPill } from '../components/StatusPill';
import { IconWrap } from '../components/IconWrap';
import { CiCalendar, CiLock } from 'react-icons/ci';
import { events, upcomingEvents, pastEvents } from './Home/data/events';
import { missions } from './Home/data/missions';
import useTheme from '../hooks/useTheme';

export const Events: React.FC = () => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past' | 'missions'>('upcoming');
  const [isRSVPModalOpen, setIsRSVPModalOpen] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getFormatBadgeColor = (format: string) => {
    switch (format) {
      case 'Virtual':
        return theme.colors.accent.korea;
      case 'In-person':
        return theme.colors.green.g1;
      case 'Hybrid':
        return theme.colors.accent.egypt;
      default:
        return theme.text.muted;
    }
  };

  const renderEventCard = (event: typeof events[0]) => (
    <Card key={event.id} hoverable>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: '14px',
              color: theme.text.muted,
              marginBottom: '8px',
            }}
          >
            {formatDate(event.date)}
          </div>
          <h3
            style={{
              fontSize: '20px',
              fontWeight: 600,
              color: theme.text.primary,
              marginBottom: '12px',
            }}
          >
            {event.title}
          </h3>
          <div
            style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '12px',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: '13px',
                color: theme.text.muted,
              }}
            >
              📍 {event.location}
            </span>
            <span
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: theme.text.primary,
                padding: '4px 10px',
                backgroundColor: getFormatBadgeColor(event.format),
                borderRadius: '6px',
              }}
            >
              {event.format}
            </span>
          </div>
          <p
            style={{
              color: theme.text.muted,
              fontSize: '14px',
              lineHeight: 1.6,
              marginBottom: '16px',
            }}
          >
            {event.description}
          </p>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <Link to={`/events/${event.slug}`} style={{ textDecoration: 'none', flex: 1 }}>
          <Button variant="secondary" style={{ width: '100%' }}>
            View Details
          </Button>
        </Link>
        <Button
          variant="primary"
          onClick={() => setIsRSVPModalOpen(true)}
          style={{ flex: 1 }}
        >
          RSVP
        </Button>
      </div>
    </Card>
  );

  return (
    <>
      <PageHero
        title="Events & Missions"
        subtitle="Upcoming events, past activities, and business missions between Korea and Egypt."
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Events & Missions' }]}
      />

      <Section>
        <PreviewDisclaimer />

        <div
          style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '32px',
            borderBottom: `1px solid ${theme.colors.line}`,
          }}
        >
          {[
            { id: 'upcoming', label: 'Upcoming Events', count: upcomingEvents.length },
            { id: 'past', label: 'Past Events', count: pastEvents.length },
            { id: 'missions', label: 'Missions', count: missions.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? `2px solid ${theme.colors.green.g1}` : '2px solid transparent',
                color: activeTab === tab.id ? theme.colors.green.g1 : theme.text.muted,
                fontSize: '16px',
                fontWeight: activeTab === tab.id ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {activeTab === 'upcoming' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map(renderEventCard)
            ) : (
              <EmptyState
                icon={<CiCalendar />}
                title="No Upcoming Events"
                description="There are no upcoming events scheduled at this time. Check back soon or join our waitlist to be notified when new events are announced."
                actions={[
                  { label: 'Join Waitlist', path: '/membership', variant: 'primary' },
                  { label: 'Contact Us', path: '/contact', variant: 'secondary' },
                  { label: 'View Missions', path: '/events', variant: 'secondary' },
                ]}
                chips={['Roundtables', 'Missions', 'Webinars']}
              />
            )}
          </div>
        )}

        {activeTab === 'past' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            {pastEvents.length > 0 ? (
              pastEvents.map(renderEventCard)
            ) : (
              <Card>
                <p style={{ color: theme.text.muted, textAlign: 'center' }}>
                  No past events to display.
                </p>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'missions' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            {missions.map((mission) => (
              <Card key={mission.id} hoverable>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: '16px',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <StatusPill status="COMING SOON" size="small" />
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: theme.colors.green.g1,
                          padding: '4px 10px',
                          backgroundColor: theme.background.panel,
                          borderRadius: '6px',
                          border: `1px solid ${theme.colors.green.g1}`,
                        }}
                      >
                        {mission.direction}
                      </span>
                      <span
                        style={{
                          fontSize: '14px',
                          color: theme.text.muted,
                        }}
                      >
                        {formatDate(mission.date)}
                      </span>
                    </div>
                    <h3
                      style={{
                        fontSize: '20px',
                        fontWeight: 600,
                        color: theme.text.primary,
                        marginBottom: '12px',
                      }}
                    >
                      {mission.title}
                    </h3>
                    <p
                      style={{
                        color: theme.text.muted,
                        fontSize: '14px',
                        lineHeight: 1.6,
                        marginBottom: '16px',
                      }}
                    >
                      {mission.description}
                    </p>
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap',
                        marginBottom: '16px',
                      }}
                    >
                      {['Infrastructure', 'Technology', 'Industrial'].map((sector, idx) => (
                        <span
                          key={idx}
                          style={{
                            fontSize: '12px',
                            color: theme.text.muted,
                            padding: '4px 8px',
                            backgroundColor: theme.background.panel,
                            borderRadius: '4px',
                            border: `1px solid ${theme.colors.line}`,
                          }}
                        >
                          {sector}
                        </span>
                      ))}
                    </div>
                    {mission.applicationDeadline && (
                      <div
                        style={{
                          fontSize: '13px',
                          color: theme.text.muted,
                          marginBottom: '16px',
                        }}
                      >
                        Application deadline: {formatDate(mission.applicationDeadline)}
                      </div>
                    )}
                  </div>
                </div>
                <Button variant="secondary" disabled style={{ width: '100%' }}>
                  <IconWrap icon={<CiLock />} size={16} color="muted" />
                  <span style={{ marginLeft: '8px' }}>Apply (Coming Soon)</span>
                </Button>
              </Card>
            ))}
          </div>
        )}
      </Section>

      <Modal
        isOpen={isRSVPModalOpen}
        onClose={() => setIsRSVPModalOpen(false)}
        title="RSVP Coming Soon"
      >
        <p style={{ color: theme.text.muted, lineHeight: 1.6 }}>
          Event RSVP functionality will be available in Phase 2 of the BCBH portal. For now, please contact us directly to express your interest in attending events.
        </p>
        <div style={{ marginTop: '24px' }}>
          <Button variant="primary" onClick={() => setIsRSVPModalOpen(false)}>
            Got it
          </Button>
        </div>
      </Modal>
    </>
  );
};
